import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OptiAssetsGate } from '@/components/optiassets/OptiAssetsGate';
import { AssetList } from '@/components/optiassets/AssetList';
import { AssetDetail } from '@/components/optiassets/AssetDetail';
import { AssetCreateDialog } from '@/components/optiassets/AssetCreateDialog';
import { OptiAssetsAnalyticsPanel } from '@/components/optiassets/OptiAssetsAnalyticsPanel';
import { JobRulesManager } from '@/components/optiassets/JobRulesManager';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { usePermissions } from '@/hooks/usePermissions';
import { Package, ArrowLeft, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function OptiAssets() {
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { isManager, isServiceWriter } = usePermissions();
  const canManageRules = isManager || isServiceWriter;

  return (
    <OptiAssetsGate>
      <div className="min-h-screen flex flex-col bg-background">
        <TopBar />
        <div className="flex-1 flex flex-col">
          {/* Page Header */}
          <div className="border-b px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Package className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold">OptiAssets</h1>
                  <p className="text-sm text-muted-foreground">Equipment tracking & analytics</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {canManageRules && (
                  <Button onClick={() => setRulesDialogOpen(true)} variant="outline" size="sm">
                    <Settings className="h-4 w-4 mr-2" />
                    Manage Job Rules
                  </Button>
                )}
                <Button onClick={() => navigate('/shop-profile?tab=admin')} variant="outline" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              </div>
            </div>
          </div>

          {/* Shop Analytics — manager-only, self-gated */}
          <OptiAssetsAnalyticsPanel />

          {/* Main Content — split on lg+; stacked on small screens with
              the detail panel hiding entirely on mobile when no asset is
              selected. Batch 3 responsive pass — no full mobile redesign. */}
          <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
            {/* Asset List */}
            <div
              className={cn(
                'border-r flex-shrink-0 overflow-hidden',
                // On small screens: full width, list-takes-all-space when no asset
                // selected, hidden when an asset is selected to give the detail
                // panel full focus.
                selectedAssetId ? 'hidden lg:block' : 'flex-1 lg:flex-none',
                'lg:w-[400px]',
              )}
            >
              <AssetList
                selectedAssetId={selectedAssetId}
                onSelectAsset={setSelectedAssetId}
                onCreateAsset={() => setCreateDialogOpen(true)}
              />
            </div>

            {/* Asset Detail */}
            <div
              className={cn(
                'flex-1 overflow-hidden',
                // On small screens: hidden when no asset selected (the list
                // takes the whole viewport); visible when an asset is picked.
                selectedAssetId ? 'flex flex-col' : 'hidden lg:flex lg:flex-col',
              )}
            >
              {selectedAssetId ? (
                <AssetDetail
                  assetId={selectedAssetId}
                  onClose={() => setSelectedAssetId(null)}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <Package className="h-16 w-16 text-muted-foreground/50 mb-4" />
                  <h2 className="text-xl font-medium text-muted-foreground">Select an Asset</h2>
                  <p className="text-sm text-muted-foreground mt-2 max-w-sm">
                    Choose an asset from the list to view details, maintenance history, and usage analytics.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Create Dialog */}
          <AssetCreateDialog
            open={createDialogOpen}
            onOpenChange={setCreateDialogOpen}
            onSuccess={(assetId) => setSelectedAssetId(assetId)}
          />

          {/* Global Job Rules dialog */}
          <Dialog open={rulesDialogOpen} onOpenChange={setRulesDialogOpen}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Job Attribution Rules</DialogTitle>
              </DialogHeader>
              <JobRulesManager />
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </OptiAssetsGate>
  );
}
